import type { Envelope, Paginated } from "./types";

const BASE = import.meta.env.VITE_API_BASE_URL ?? "";
const PREFIX = `${BASE}/api/v1`;

/** An image path the API returns relative to its own host (category icons). */
export function assetUrl(path: string): string {
  return /^https?:\/\//.test(path) ? path : `${BASE}${path}`;
}

const ACCESS_KEY = "kheti24.access";
const REFRESH_KEY = "kheti24.refresh";

/** Error carrying the backend's machine-readable code alongside the message. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }

  get isAuth(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }
}

export const tokenStore = {
  get access(): string | null {
    return localStorage.getItem(ACCESS_KEY);
  },
  get refresh(): string | null {
    return localStorage.getItem(REFRESH_KEY);
  },
  set(access: string, refresh: string) {
    localStorage.setItem(ACCESS_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

/** Fires when refresh fails, so the app can bounce to /login. */
type LogoutHandler = () => void;
let onLogout: LogoutHandler = () => {};
export function setLogoutHandler(handler: LogoutHandler) {
  onLogout = handler;
}

async function parseError(res: Response): Promise<ApiError> {
  let code = "UNKNOWN";
  let message = res.statusText || "Request failed";
  let details: unknown;
  try {
    const body = await res.json();
    if (body?.error) {
      code = body.error.code ?? code;
      message = body.error.message ?? message;
      details = body.error.details;
    }
  } catch {
    // Non-JSON body (proxy error page, 502, …) — keep the status text.
  }
  return new ApiError(res.status, code, message, details);
}

// Single-flight refresh: many queries can 401 at once, but only one refresh
// request should go out, and the rest wait on it.
let refreshInFlight: Promise<boolean> | null = null;

async function refreshTokens(): Promise<boolean> {
  const refresh = tokenStore.refresh;
  if (!refresh) return false;

  const res = await fetch(`${PREFIX}/admin/auth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh_token: refresh }),
  });

  if (!res.ok) {
    tokenStore.clear();
    return false;
  }
  const body = (await res.json()) as Envelope<{
    access_token: string;
    refresh_token: string;
  }>;
  tokenStore.set(body.data.access_token, body.data.refresh_token);
  return true;
}

function ensureRefresh(): Promise<boolean> {
  refreshInFlight ??= refreshTokens().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  params?: Record<string, string | number | boolean | undefined | null>;
  /** Skip the bearer header — used by login, which has no token yet. */
  anonymous?: boolean;
  signal?: AbortSignal;
}

function buildUrl(path: string, params?: RequestOptions["params"]): string {
  // The second argument only supplies a base when PREFIX is relative (dev,
  // where the vite proxy makes /api same-origin). When VITE_API_BASE_URL is
  // set, PREFIX is absolute and the base is ignored.
  const url = new URL(`${PREFIX}${path}`, window.location.origin);
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === null || value === "") continue;
      url.searchParams.set(key, String(value));
    }
  }
  // Must return the full URL, not pathname+search: dropping the origin makes
  // fetch resolve against the PAGE's origin, so a console on its own domain
  // would call itself instead of the API and get caddy's 405 from the static
  // file server. Same-origin deployments are unaffected — toString() just
  // rebuilds the origin they already had.
  return url.toString();
}

async function rawRequest(
  path: string,
  options: RequestOptions,
  retrying = false,
): Promise<Response> {
  const headers: Record<string, string> = {};
  // FormData sets its own multipart Content-Type, boundary included.
  const isForm = options.body instanceof FormData;
  if (options.body !== undefined && !isForm) headers["Content-Type"] = "application/json";
  if (!options.anonymous) {
    const token = tokenStore.access;
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(buildUrl(path, options.params), {
    method: options.method ?? "GET",
    headers,
    body:
      options.body === undefined
        ? undefined
        : isForm
          ? (options.body as FormData)
          : JSON.stringify(options.body),
    signal: options.signal,
  });

  // One transparent refresh-and-retry on 401, then give up and log out.
  if (res.status === 401 && !options.anonymous && !retrying) {
    const refreshed = await ensureRefresh();
    if (refreshed) return rawRequest(path, options, true);
    tokenStore.clear();
    onLogout();
  }

  return res;
}

export async function request<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const res = await rawRequest(path, options);
  if (!res.ok) throw await parseError(res);
  if (res.status === 204) return undefined as T;
  const body = (await res.json()) as Envelope<T>;
  return body.data;
}

/** Same as `request`, but keeps the pagination meta the list screens need. */
export async function requestPaginated<T>(
  path: string,
  options: RequestOptions = {},
): Promise<Paginated<T>> {
  const res = await rawRequest(path, options);
  if (!res.ok) throw await parseError(res);
  const body = (await res.json()) as Envelope<T[]>;
  return {
    items: body.data ?? [],
    page: body.meta?.page ?? 1,
    pageSize: body.meta?.page_size ?? 20,
    total: body.meta?.total ?? 0,
  };
}

export const api = {
  get: <T>(path: string, params?: RequestOptions["params"]) =>
    request<T>(path, { params }),
  list: <T>(path: string, params?: RequestOptions["params"]) =>
    requestPaginated<T>(path, { params }),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PATCH", body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: "PUT", body }),
  upload: <T>(path: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return request<T>(path, { method: "POST", body: form });
  },
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
  anonymousPost: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body, anonymous: true }),
};
